<script>
import signaturesAPI from "@/services/signatures/signaturesAPI"
import Swal from "sweetalert2"

export default {
  name: "SignaturePad",
  props: {
    visible: { type: Boolean, default: false },
    companyId: { type: [Number, String], required: true },
    documentTitle: { type: String, default: "Documento" },
    roleFilter: { type: String, default: null },
  },
  data() {
    return {
      signatures: [],
      selectedId: null,
      consent: false,
      loading: false,
      submitting: false,
    }
  },
  computed: {
    canSign() {
      return this.selectedId && this.consent && !this.submitting
    },
  },
  watch: {
    visible(v) {
      if (v) this.load()
    },
  },
  mounted() {
    // Portal manual (Vue 2 no tiene <teleport>): movemos la raíz del modal al <body>
    // para que el overlay `position: fixed` NO quede atrapado en stacking contexts de
    // ancestros (p.ej. el `position: sticky` de DocumentActions o transforms del layout),
    // que harían que el form se superponga al modal. Ver PATRON_FORMATO_FIRMADO §7.4.
    if (this.$el && document.body) {
      document.body.appendChild(this.$el)
    }
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  methods: {
    async load() {
      this.loading = true
      this.selectedId = null
      this.consent = false
      try {
        const res = await signaturesAPI.index(this.companyId)
        const all = (res && res.data) || []
        this.signatures = this.roleFilter
          ? all.filter(s => s.role === this.roleFilter)
          : all
        // Firma única (Responsable SG-SST): si solo hay una, se preselecciona.
        if (this.signatures.length === 1) {
          this.selectedId = this.signatures[0].id
        }
      } catch (e) {
        Swal.fire("Error", "No se pudieron cargar las firmas", "error")
      } finally {
        this.loading = false
      }
    },
    close() {
      this.$emit("close")
    },
    async confirm() {
      if (!this.canSign) return
      this.submitting = true
      try {
        this.$emit("sign", { signature_id: this.selectedId })
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<template>
  <!-- Raíz persistente: se mueve al <body> en mounted (portal) para escapar de
       stacking contexts de ancestros. El overlay solo se monta cuando visible. -->
  <div class="sp-portal">
    <div v-if="visible" class="sp-overlay" @click.self="close">
    <div class="sp-modal">
      <div class="sp-header">
        <h5>Firmar {{ documentTitle }}</h5>
        <button class="sp-close" @click="close">×</button>
      </div>

      <div class="sp-body">
        <div v-if="loading" class="text-center my-4">
          <b-spinner variant="primary" small></b-spinner>
        </div>

        <div v-else-if="signatures.length === 0" class="alert alert-warning">
          No hay firma registrada para esta empresa. Configúrala primero en
          <strong>Editar empresa → Identidad y firmas</strong>.
        </div>

        <div v-else>
          <p class="text-muted small mb-2">Selecciona la firma a utilizar:</p>
          <div class="sp-sig-list">
            <label
              v-for="sig in signatures"
              :key="sig.id"
              class="sp-sig-item"
              :class="{ active: selectedId === sig.id }"
            >
              <input type="radio" :value="sig.id" v-model="selectedId" />
              <img v-if="sig.image_url" :src="sig.image_url" class="sp-sig-img" />
              <div class="sp-sig-meta">
                <strong>{{ sig.signer_name }}</strong>
                <div class="small text-muted">
                  {{ sig.signer_position || "Responsable SG-SST" }}
                  <span v-if="sig.signer_document"> · CC {{ sig.signer_document }}</span>
                  <span v-if="sig.signer_license"> · Lic. SST {{ sig.signer_license }}</span>
                </div>
              </div>
            </label>
          </div>

          <hr class="my-3" />

          <label class="sp-consent">
            <input type="checkbox" v-model="consent" />
            Confirmo que la información es veraz y que la firma electrónica es válida
            según la Ley 527/1999 y el Decreto 2364/2012.
          </label>
        </div>
      </div>

      <div class="sp-footer">
        <button class="btn btn-light btn-sm" @click="close">Cancelar</button>
        <button class="btn btn-primary btn-sm" :disabled="!canSign" @click="confirm">
          <span v-if="submitting"><b-spinner small></b-spinner></span>
          <span v-else>Firmar</span>
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
.sp-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1050;
}
.sp-modal {
  background: #fff; border-radius: 8px; width: 100%; max-width: 520px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}
.sp-header {
  padding: 14px 18px; border-bottom: 1px solid #eee;
  display: flex; justify-content: space-between; align-items: center;
}
.sp-header h5 { margin: 0; }
.sp-close {
  background: none; border: none; font-size: 24px; line-height: 1; cursor: pointer;
}
.sp-body { padding: 18px; max-height: 60vh; overflow-y: auto; }
.sp-footer {
  padding: 12px 18px; border-top: 1px solid #eee;
  display: flex; justify-content: flex-end; gap: 8px;
}
.sp-sig-list { display: flex; flex-direction: column; gap: 8px; }
.sp-sig-item {
  display: flex; align-items: center; gap: 12px;
  padding: 10px; border: 1px solid #ddd; border-radius: 6px;
  cursor: pointer; transition: all 0.15s;
}
.sp-sig-item:hover { background: #f7faff; }
.sp-sig-item.active { border-color: #1f4e8c; background: #eaf1fa; }
.sp-sig-item input[type="radio"] { margin: 0; }
.sp-sig-img { max-height: 40px; max-width: 120px; }
.sp-sig-meta { flex: 1; }
.sp-consent { display: flex; gap: 8px; font-size: 0.85rem; cursor: pointer; }
</style>
